import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Stack,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import {
  useCurrentUserQuery,
  useUpdateUserMutation,
  useChangePasswordMutation,
  useChangeProfilePictureMutation,
} from '@/features/useraccount/api';

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z
      .string()
      .regex(/\d/, { message: 'hasNumber' })
      .regex(/[A-Z]/, { message: 'hasCapitalCase' })
      .regex(/[a-z]/, { message: 'hasSmallCase' })
      .regex(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, { message: 'hasSpecialCharacters' })
      .min(6, { message: 'hasMinLength' }),
    confirmPassword: z.string().min(1),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'mustMatch',
  });

type PasswordForm = z.infer<typeof passwordSchema>;

const nameSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
});
type NameForm = z.infer<typeof nameSchema>;

export interface ProfileModalProps {
  onClose: (value: null) => void;
}

export function ProfileModal({ onClose }: ProfileModalProps) {
  const { t } = useTranslation();
  const { data: user, isLoading } = useCurrentUserQuery();
  const updateUser = useUpdateUserMutation();
  const changePassword = useChangePasswordMutation();
  const changePicture = useChangeProfilePictureMutation();

  const [modeEditName, setModeEditName] = useState(false);
  const [modeEditPassword, setModeEditPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameForm = useForm<NameForm>({
    resolver: zodResolver(nameSchema),
    values: {
      first_name: user?.firstName ?? '',
      last_name: user?.lastName ?? '',
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmitName = nameForm.handleSubmit(async (values) => {
    if (!user) return;
    setError(null);
    const res = await updateUser.mutateAsync({
      userId: user.userId,
      firstname: values.first_name,
      lastname: values.last_name,
    });
    if (res.code === 0) {
      setModeEditName(false);
    } else {
      setError(res.message);
    }
  });

  const onSubmitPassword = passwordForm.handleSubmit(async (values) => {
    if (!user) return;
    setError(null);
    const res = await changePassword.mutateAsync({
      username: user.username,
      newPassword: values.newPassword,
      previousPassword: values.currentPassword,
    });
    if (res.code === 0) {
      passwordForm.reset();
      setModeEditPassword(false);
    } else {
      setError(res.message);
    }
  });

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    const res = await changePicture.mutateAsync(fd);
    if (res.code !== 0) setError(res.message);
  };

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('pages.profile.profile_label', { defaultValue: 'Profile' })}
        <IconButton onClick={() => onClose(null)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading || !user ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar src={user.picture} sx={{ width: 96, height: 96 }} />
                <IconButton
                  component="label"
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <PhotoCameraIcon fontSize="small" />
                  <input hidden type="file" accept="image/*" onChange={handlePictureChange} />
                </IconButton>
              </Box>
              <Box>
                <Typography variant="h6">
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.profile}
                </Typography>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">
                  {t('pages.profile.personal_info', { defaultValue: 'Personal information' })}
                </Typography>
                {!modeEditName && (
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setModeEditName(true)}>
                    {t('global.actions.edit', { defaultValue: 'Edit' })}
                  </Button>
                )}
              </Stack>
              {modeEditName ? (
                <Box component="form" onSubmit={onSubmitName} sx={{ mt: 2 }}>
                  <Stack spacing={2}>
                    <Controller
                      name="first_name"
                      control={nameForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t('pages.profile.first_name', { defaultValue: 'First name' })}
                          error={!!fieldState.error}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Controller
                      name="last_name"
                      control={nameForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t('pages.profile.last_name', { defaultValue: 'Last name' })}
                          error={!!fieldState.error}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button onClick={() => setModeEditName(false)}>
                        {t('global.actions.cancel', { defaultValue: 'Cancel' })}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={updateUser.isPending || !nameForm.formState.isValid}
                      >
                        {t('global.actions.save', { defaultValue: 'Save' })}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ) : null}
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">
                  {t('pages.profile.password', { defaultValue: 'Password' })}
                </Typography>
                {!modeEditPassword && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setModeEditPassword(true)}
                  >
                    {t('pages.profile.change_password', { defaultValue: 'Change password' })}
                  </Button>
                )}
              </Stack>
              {modeEditPassword ? (
                <Box component="form" onSubmit={onSubmitPassword} sx={{ mt: 2 }}>
                  <Stack spacing={2}>
                    <Controller
                      name="currentPassword"
                      control={passwordForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="password"
                          label={t('pages.profile.current_password', { defaultValue: 'Current password' })}
                          error={!!fieldState.error}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Controller
                      name="newPassword"
                      control={passwordForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="password"
                          label={t('pages.profile.new_password', { defaultValue: 'New password' })}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Controller
                      name="confirmPassword"
                      control={passwordForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="password"
                          label={t('pages.profile.confirm_password', { defaultValue: 'Confirm password' })}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        onClick={() => {
                          passwordForm.reset();
                          setModeEditPassword(false);
                        }}
                      >
                        {t('global.actions.cancel', { defaultValue: 'Cancel' })}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={changePassword.isPending || !passwordForm.formState.isValid}
                      >
                        {t('global.actions.save', { defaultValue: 'Save' })}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ) : null}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>
          {t('global.actions.close', { defaultValue: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
